import React, { useState, useEffect } from 'react';
import { C } from './SettingsTokens';
import { BackIcon, ChevronRight } from './SettingsIcons';
import { PAKET_META, TONLAGEN, THEMES } from './SettingsConstants';
import { SubPageHeader, LabeledInput } from './SettingsComponents';
import MFAEinstellungen from '../components/MFAEinstellungen';
import { 
  getNickname, setNickname, 
  abrechnungAbrufen, preiseAbrufen, 
  checkoutSessionErstellen 
} from '../api';

export function IdentitaetSeite({ nutzer, onBack }) {
  const [nickname, setNicknameVal] = useState('');
  const [laden, setLaden] = useState(true);
  const [gespeichert, setGespeichert] = useState(false);

  const anzeigename = nutzer?.displayName || '';
  const vorname = anzeigename.split(' ')[0] || '';
  const nachname = anzeigename.split(' ').slice(1).join(' ') || '';
  const initiale = ((nickname || vorname || nutzer?.email || '?')[0]).toUpperCase();

  useEffect(() => {
    getNickname()
      .then(r => setNicknameVal(r.data.nickname || ''))
      .catch(() => {})
      .finally(() => setLaden(false));
  }, []);

  const speichern = async () => {
    try {
      await setNickname(nickname);
      setGespeichert(true);
      setTimeout(() => setGespeichert(false), 2500);
    } catch {}
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: C.bgGrad, fontFamily: C.font }}>
      <SubPageHeader title="Identität" subtitle="Vorname, Nachname, Spitzname" onBack={onBack} />
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '0 16px' }}>
        <div style={{
          background: C.surfaceAlt, borderRadius: 16, border: `1px solid ${C.border}`,
          padding: '16px 18px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 14,
          boxShadow: C.shadow,
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14, flexShrink: 0,
            background: `linear-gradient(135deg, ${C.teal}, #006A57)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, fontWeight: 700, color: '#FBF7ED',
            boxShadow: `0 4px 14px rgba(0,77,64,0.28)`,
          }}>{initiale}</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>Profilbild</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>In einer späteren Version verfügbar</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          {[['Vorname', vorname], ['Nachname', nachname]].map(([lbl, val]) => (
            <div key={lbl} style={{ flex: 1 }}>
              <LabeledInput label={lbl} value={val} readOnly />
            </div>
          ))}
        </div>
        <div style={{
          background: C.tealSoft, borderLeft: `3px solid ${C.amber}`,
          borderRadius: '0 12px 12px 0', padding: '11px 14px', marginBottom: 14,
          fontSize: 13, color: C.textMuted, lineHeight: 1.55,
        }}>
          Wie soll die KI-Assistentin dich ansprechen?
        </div>
        <LabeledInput
          label="Spitzname"
          value={laden ? '' : nickname}
          onChange={e => setNicknameVal(e.target.value)}
          placeholder={vorname || 'z. B. Max, Alex …'}
        />
        <button
          onClick={speichern}
          style={{
            width: '100%', padding: '14px', marginTop: 16,
            background: gespeichert ? C.green : C.amber,
            color: gespeichert ? '#fff' : C.teal,
            border: 'none', borderRadius: 999,
            fontFamily: C.font, fontSize: 15, fontWeight: 700,
            cursor: 'pointer',
            boxShadow: gespeichert
              ? `0 4px 16px rgba(0,128,106,0.3)`
              : `0 4px 18px rgba(255,188,0,0.4)`,
            transition: 'background 0.25s, box-shadow 0.25s',
            WebkitTapHighlightColor: 'transparent',
            marginBottom: 'calc(env(safe-area-inset-bottom, 0px) + 32px)',
          }}
        >
          {gespeichert ? '✓ Gespeichert' : 'Spitzname speichern'}
        </button>
      </div>
    </div>
  );
}

export function AccountSeite({ nutzer, onBack }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: C.bgGrad, fontFamily: C.font }}>
      <SubPageHeader title="Account-Verifizierung" subtitle="E-Mail · Sicherheit · 2FA" onBack={onBack} />
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '0 16px' }}>
        <div style={{
          background: C.surfaceAlt, borderRadius: 16, border: `1px solid ${C.border}`,
          padding: '14px 16px', marginBottom: 12, boxShadow: C.shadow,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.textDim, marginBottom: 6 }}>
            E-Mail
          </div>
          <div style={{ fontSize: 14, fontWeight: 500, color: C.text }}>{nutzer?.email || '—'}</div>
        </div>
        <div style={{
          background: nutzer?.emailVerified ? C.greenSoft : C.redSoft,
          borderRadius: 14, border: `1px solid ${nutzer?.emailVerified ? 'rgba(0,128,106,0.18)' : 'rgba(192,57,43,0.18)'}`,
          padding: '12px 16px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: 999, flexShrink: 0,
            background: nutzer?.emailVerified ? C.greenSoft : C.redSoft,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              {nutzer?.emailVerified
                ? <path d="M2 7l2.5 2.5 7-7" stroke={C.green} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                : <path d="M3 3l8 8M11 3L3 11" stroke={C.red} strokeWidth="1.8" strokeLinecap="round"/>
              }
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: nutzer?.emailVerified ? C.green : C.red }}>
              {nutzer?.emailVerified ? 'E-Mail bestätigt' : 'E-Mail nicht bestätigt'}
            </div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 1 }}>
              {nutzer?.emailVerified
                ? 'Deine E-Mail-Adresse ist verifiziert.'
                : 'Bitte bestätige deine E-Mail-Adresse.'}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 10 }}>
          Zwei-Faktor-Authentifizierung
        </div>
        <div style={{
          background: C.surfaceAlt, borderRadius: 16, border: `1px solid ${C.border}`,
          overflow: 'hidden', boxShadow: C.shadow,
          marginBottom: 'calc(env(safe-area-inset-bottom, 0px) + 32px)',
        }}>
          <MFAEinstellungen nutzer={nutzer} />
        </div>
      </div>
    </div>
  );
}

export function AbrechnungSeite({ nutzer, onBack }) {
  const [info, setInfo]         = useState(null);
  const [laden, setLaden]       = useState(true);
  const [preise, setPreise]     = useState([]);
  const [preiseLaden, setPreiseLaden] = useState(false);
  const [zeigeKaufen, setZeigeKaufen] = useState(false);
  const [kauftGrade, setKauftGrade]   = useState(false);

  useEffect(() => {
    abrechnungAbrufen()
      .then(r => setInfo(r.data))
      .catch(() => {})
      .finally(() => setLaden(false));
  }, []);

  const oeffneKaufen = () => {
    setZeigeKaufen(true);
    if (preise.length === 0) {
      setPreiseLaden(true);
      preiseAbrufen()
        .then(r => setPreise(r.data.filter(p => p.paket_typ !== 'free')))
        .catch(() => {})
        .finally(() => setPreiseLaden(false));
    }
  };

  const handleKaufen = async (preisId) => {
    setKauftGrade(true);
    try {
      const r = await checkoutSessionErstellen(preisId, new Date().toISOString());
      window.location.href = r.data.checkout_url;
    } catch { setKauftGrade(false); }
  };

  const sub       = info?.subscription ?? null;
  const aboAktiv  = !!(info?.abo_aktiv || info?.unbegrenzt);
  const planLabel = aboAktiv ? 'Pro' : 'Free';
  const expiresAt = sub?.expires_at
    ? new Date(sub.expires_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })
    : null;

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%', background: C.bgGrad, fontFamily: C.font, overflow: 'hidden' }}>
      <SubPageHeader title="Abrechnung" subtitle="Abonnement" onBack={onBack} />
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {laden ? (
          <div style={{ padding: '32px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[80, 56, 48].map(h => (
              <div key={h} style={{ height: h, borderRadius: 14, background: 'rgba(0,77,64,0.07)', animation: 'pulse 1.4s ease-in-out infinite' }} />
            ))}
          </div>
        ) : (
          <>
            <div style={{ padding: '20px 20px 0' }}>
              <div style={{
                background: `linear-gradient(145deg, ${C.teal} 0%, #005948 55%, #003D30 100%)`,
                borderRadius: 24, padding: '26px 24px 22px', boxShadow: '0 8px 32px rgba(0,77,64,0.28), 0 2px 8px rgba(0,0,0,0.12)',
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', top: -20, right: -20, width: 110, height: 110, borderRadius: '50%', background: 'rgba(255,188,0,0.10)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: -30, left: 60, width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(251,247,237,0.5)', marginBottom: 10 }}>Aktueller Plan</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#FBF7ED', letterSpacing: -0.5, lineHeight: 1 }}>{planLabel}</div>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    background: aboAktiv ? 'rgba(255,188,0,0.20)' : 'rgba(255,255,255,0.10)',
                    border: `1px solid ${aboAktiv ? 'rgba(255,188,0,0.35)' : 'rgba(255,255,255,0.15)'}`,
                    borderRadius: 999, padding: '5px 13px',
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase',
                    color: aboAktiv ? C.amber : 'rgba(251,247,237,0.45)',
                  }}><div style={{ width: 5, height: 5, borderRadius: 999, background: 'currentColor' }} />{aboAktiv ? 'Aktiv' : 'Inaktiv'}</div>
                </div>
                {aboAktiv && (
                  <>
                    <div style={{ height: 1, background: 'rgba(251,247,237,0.10)', marginBottom: 16 }} />
                    <div style={{ fontSize: 13, color: 'rgba(251,247,237,0.65)', lineHeight: 1.5 }}>Unbegrenzte Nutzung des Agenten-Workflows</div>
                  </>
                )}
                {expiresAt && <div style={{ marginTop: 14, fontSize: 11, color: 'rgba(251,247,237,0.35)' }}>Verlängert am {expiresAt}</div>}
              </div>
            </div>
            <div style={{ padding: '12px 20px 0' }}>
              <div style={{ background: C.surfaceAlt, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: C.shadow, overflow: 'hidden' }}>
                {[['E-Mail', nutzer?.email || '—']].map(([lbl, val]) => (
                  <div key={lbl} style={{ padding: '13px 18px' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.11em', color: C.textDim, marginBottom: 3 }}>{lbl}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>
            {!aboAktiv && (
              <div style={{ padding: '16px 20px 0' }}>
                <button
                  onClick={oeffneKaufen}
                  style={{
                    width: '100%', padding: '16px 24px', borderRadius: 16, border: 'none',
                    background: C.amber, color: C.teal, fontSize: 15, fontWeight: 800,
                    cursor: 'pointer', boxShadow: '0 4px 20px rgba(255,188,0,0.40), 0 1px 4px rgba(0,0,0,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    WebkitTapHighlightColor: 'transparent',
                  }}
                  onTouchStart={e => e.currentTarget.style.transform = 'scale(0.97)'}
                  onTouchEnd={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  Abo abschließen →
                </button>
              </div>
            )}
            <div style={{ padding: '14px 20px 0', textAlign: 'center' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.textDim }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                Monatlich kündbar · Stripe · SSL-gesichert
              </div>
            </div>
            <div style={{ height: 'calc(env(safe-area-inset-bottom, 0px) + 40px)' }} />
          </>
        )}
      </div>

      <div style={{
        position: 'absolute', inset: 0, zIndex: 20, background: C.bgGrad, fontFamily: C.font,
        display: 'flex', flexDirection: 'column', transform: zeigeKaufen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)', borderBottom: `1px solid ${C.border}`, background: C.surface }}>
          <button onClick={() => setZeigeKaufen(false)} style={{ width: 36, height: 36, borderRadius: 999, border: 'none', background: C.tealSoft, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.teal, WebkitTapHighlightColor: 'transparent' }}><BackIcon /></button>
          <div><div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Abo wählen</div><div style={{ fontSize: 12, color: C.textMuted }}>Monatlich kündbar</div></div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {preiseLaden ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '24px 20px' }}>
              {[1, 2, 3].map(i => <div key={i} style={{ height: 120, borderRadius: 20, background: 'rgba(0,77,64,0.07)' }} />)}
            </div>
          ) : (
            <div style={{ padding: '20px 20px', display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}>
              {preise.map((preis) => {
                const meta = PAKET_META[preis.name] ?? {};
                const star = meta.tagStar ?? preis.beliebt;
                const tag  = meta.tag ?? (preis.beliebt ? 'Bestseller' : null);
                const features = meta.features ?? (preis.beschreibung ? [preis.beschreibung] : []);
                const preisBetrag = preis.betrag ? (preis.betrag / 100).toFixed(2).replace('.', ',') : '—';
                return (
                  <div key={preis.id} style={{ background: star ? `linear-gradient(150deg, ${C.teal} 0%, #005948 60%, #003D30 100%)` : C.surfaceAlt, borderRadius: 20, border: star ? 'none' : `1.5px solid ${C.border}`, padding: '22px 20px 18px', boxShadow: star ? '0 12px 40px rgba(0,77,64,0.30)' : C.shadowMd, position: 'relative', overflow: 'hidden' }}>
                    {star && <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,188,0,0.12)', pointerEvents: 'none' }} />}
                    {tag && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 11px', borderRadius: 999, marginBottom: 14, background: star ? 'rgba(255,188,0,0.20)' : C.tealSoft, border: `1px solid ${star ? 'rgba(255,188,0,0.35)' : C.border}`, fontSize: 10, fontWeight: 800, letterSpacing: '0.10em', textTransform: 'uppercase', color: star ? C.amber : C.teal }}>{star && <span>★ </span>}{tag}</div>}
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: star ? 'rgba(251,247,237,0.6)' : C.textMuted, marginBottom: 2 }}>{preis.name}</div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: star ? 'rgba(251,247,237,0.7)' : C.textMuted }}>€</span>
                          <span style={{ fontSize: 36, fontWeight: 900, color: star ? '#FBF7ED' : C.text, letterSpacing: -1.5, lineHeight: 1 }}>{preisBetrag.split(',')[0]}</span>
                          <span style={{ fontSize: 16, fontWeight: 700, color: star ? 'rgba(251,247,237,0.7)' : C.textMuted }}>,{preisBetrag.split(',')[1] ?? '00'}</span>
                          <span style={{ fontSize: 12, color: star ? 'rgba(251,247,237,0.45)' : C.textDim, marginLeft: 3 }}>/Monat</span>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 18 }}>
                      {features.map((f, fi) => (
                        <div key={fi} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '8px 0', borderTop: fi > 0 ? `1px solid ${star ? 'rgba(251,247,237,0.09)' : C.border}` : 'none' }}>
                          <div style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginTop: 1, background: star ? 'rgba(255,188,0,0.20)' : C.tealSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M2 5.5L4 7.5L8.5 3" stroke={star ? C.amber : C.teal} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </div>
                          <span style={{ fontSize: 13, color: star ? 'rgba(251,247,237,0.82)' : C.textMuted, lineHeight: 1.5 }}>{f}</span>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => handleKaufen(preis.id)}
                      disabled={kauftGrade}
                      style={{ width: '100%', padding: '14px', borderRadius: 14, border: 'none', background: star ? C.amber : C.teal, color: star ? C.teal : '#FBF7ED', fontSize: 14, fontWeight: 800, cursor: kauftGrade ? 'default' : 'pointer', boxShadow: star ? '0 4px 16px rgba(255,188,0,0.38)' : 'none', WebkitTapHighlightColor: 'transparent' }}
                    >
                      {kauftGrade ? 'Weiterleitung…' : 'Jetzt abonnieren →'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function DesignSeite({ onBack, tonlage = 'klassisch', onTonlageChange, themeId, onThemeChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: C.bgGrad, fontFamily: C.font }}>
      <SubPageHeader title="Design & KI" subtitle="Schreibstil und Farbschema" onBack={onBack} />
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '0 16px' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 10 }}>Schreibstil der KI</div>
        <div style={{ background: C.surfaceAlt, borderRadius: 16, border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: C.shadow, marginBottom: 22 }}>
          {TONLAGEN.map((t, i) => {
            const active = tonlage === t.id;
            return (
              <button key={t.id} onClick={() => onTonlageChange(t.id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '13px 16px', textAlign: 'left', background: active ? C.amberSoft : 'transparent', border: 'none', borderBottom: i < TONLAGEN.length - 1 ? `1px solid ${C.border}` : 'none', cursor: 'pointer', fontFamily: C.font, WebkitTapHighlightColor: 'transparent', transition: 'background 0.12s' }}>
                <div><div style={{ fontSize: 14, fontWeight: active ? 600 : 400, color: active ? C.teal : C.text }}>{t.label}</div><div style={{ fontSize: 12, color: C.textMuted, marginTop: 1 }}>{t.desc}</div></div>
                {active && (<div style={{ width: 20, height: 20, borderRadius: 999, background: C.amber, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2 2 4-4" stroke={C.teal} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg></div>)}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 10 }}>Farbschema</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 'calc(env(safe-area-inset-bottom, 0px) + 32px)' }}>
          {THEMES.map(theme => {
            const active = themeId === theme.id;
            return (
              <button key={theme.id} onClick={() => onThemeChange(theme.id)} style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', padding: '14px 16px', textAlign: 'left', background: active ? C.amberSoft : C.surfaceAlt, border: `1.5px solid ${active ? C.amber : C.border}`, borderRadius: 14, cursor: 'pointer', fontFamily: C.font, WebkitTapHighlightColor: 'transparent', transition: 'all 0.15s', boxShadow: C.shadow }}>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>{theme.colors.map((col, i) => (<div key={i} style={{ width: i === 0 ? 30 : 14, height: 30, borderRadius: 7, background: col, border: '1px solid rgba(0,0,0,0.08)' }} />))}</div>
                <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{theme.label}</div><div style={{ fontSize: 12, color: C.textMuted }}>{theme.subtitle}</div></div>
                {active && (<div style={{ width: 20, height: 20, borderRadius: 999, background: C.amber, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2 2 4-4" stroke={C.teal} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg></div>)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function BenachrichtigungenSeite({ onBack }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: C.bgGrad, fontFamily: C.font }}>
      <SubPageHeader title="Benachrichtigungen" onBack={onBack} />
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '0 16px' }}>
        <div style={{ background: C.surfaceAlt, borderRadius: 16, border: `1px solid ${C.border}`, padding: '32px 20px', textAlign: 'center', boxShadow: C.shadow }}>
          <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.25 }}>🔔</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 6 }}>Demnächst verfügbar</div>
          <div style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.55 }}>Push-Benachrichtigungen und E-Mail-Einstellungen kommen in einer der nächsten Versionen.</div>
        </div>
      </div>
    </div>
  );
}

export function DatenschutzSeite({ onBack }) {
  const links = [
    { label: 'Datenschutzerklärung', href: 'https://cvflow.de/datenschutz' },
    { label: 'Allgemeine Geschäftsbedingungen', href: 'https://cvflow.de/agb' },
    { label: 'Impressum', href: 'https://cvflow.de/impressum' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: C.bgGrad, fontFamily: C.font }}>
      <SubPageHeader title="Datenschutz" subtitle="Rechtliches & Transparenz" onBack={onBack} />
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '0 16px' }}>
        <div style={{ background: C.surfaceAlt, borderRadius: 16, border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: C.shadow, marginBottom: 20 }}>
          {links.map((l, i) => (
            <a key={l.href} href={l.href} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', textDecoration: 'none', borderBottom: i < links.length - 1 ? `1px solid ${C.border}` : 'none', color: C.text, fontSize: 15, fontWeight: 500 }}>{l.label}<ChevronRight /></a>
          ))}
        </div>
        <div style={{ background: C.tealSoft, borderRadius: 14, border: `1px solid ${C.border}`, padding: '14px 16px', marginBottom: 'calc(env(safe-area-inset-bottom, 0px) + 32px)', fontSize: 13, color: C.textMuted, lineHeight: 1.6 }}>CVFlow speichert deine Daten sicher und gibt sie nicht an Dritte weiter. Du kannst dein Konto und alle zugehörigen Daten jederzeit löschen.</div>
      </div>
    </div>
  );
}
